import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { POSOrderWithItems } from '../types/admin';

export function useOrderHistory(filters?: {
  startDate?: Date;
  endDate?: Date;
  status?: 'completed' | 'cancelled';
  searchTerm?: string;
}) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['pos-order-history', company?.id, filters],
    queryFn: async (): Promise<POSOrderWithItems[]> => {
      if (!company?.id) return [];

      let query = supabase
        .from('pos_orders')
        .select('*')
        .eq('company_id', company.id)
        .in('status', ['completed', 'cancelled'])
        .order('completed_at', { ascending: false })
        .limit(100);

      // Apply filters
      if (filters?.startDate) {
        query = query.gte('completed_at', filters.startDate.toISOString());
      }
      if (filters?.endDate) {
        query = query.lte('completed_at', filters.endDate.toISOString());
      }
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }
      if (filters?.searchTerm) {
        query = query.or(`order_number.ilike.%${filters.searchTerm}%,customer_name.ilike.%${filters.searchTerm}%`);
      }

      const { data: orders, error: ordersError } = await query;

      if (ordersError) throw ordersError;
      if (!orders || orders.length === 0) return [];

      // Get items for all orders
      const orderIds = orders.map(o => o.id);
      const { data: items, error: itemsError } = await supabase
        .from('pos_order_items')
        .select('*')
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // Group items by order
      return orders.map(order => ({
        ...order,
        items: (items || []).filter(item => item.order_id === order.id),
      })) as POSOrderWithItems[];
    },
    enabled: !!company?.id,
  });
}

export function useDailySalesStats(date?: Date) {
  const { company } = useAuthStore();
  const targetDate = date || new Date();

  return useQuery({
    queryKey: ['pos-daily-sales', company?.id, targetDate.toDateString()],
    queryFn: async () => {
      if (!company?.id) return null;

      const startOfDay = new Date(targetDate);
      startOfDay.setHours(0, 0, 0, 0);
      
      const endOfDay = new Date(targetDate);
      endOfDay.setHours(23, 59, 59, 999);

      const { data: orders, error } = await supabase
        .from('pos_orders')
        .select('total_amount, status')
        .eq('company_id', company.id)
        .eq('status', 'completed')
        .gte('completed_at', startOfDay.toISOString())
        .lte('completed_at', endOfDay.toISOString());

      if (error) throw error;

      const totalSales = orders?.reduce((sum, o) => sum + o.total_amount, 0) || 0;
      const orderCount = orders?.length || 0;
      const averageOrder = orderCount > 0 ? totalSales / orderCount : 0;

      return {
        totalSales,
        orderCount,
        averageOrder,
      };
    },
    enabled: !!company?.id,
  });
}
